import { PDFDocument } from 'pdf-lib'

// Letter, in PDF points. Each image attachment gets its own page, in the orientation that matches the
// image, so a landscape photo isn't shrunk to fit a portrait page.
const PAGE_LONG_EDGE = 792
const PAGE_SHORT_EDGE = 612
const PAGE_MARGIN = 18

// Re-encode quality, used only when a photo has to be redrawn to bake in its EXIF rotation.
const JPEG_QUALITY = 0.92

function hasSignature(bytes, signature) {
  return signature.every((byte, index) => bytes[index] === byte)
}

// Detect by magic bytes rather than file extension or content type: the stored originalFileName is
// whatever the phone called the file, and is not reliable.
function isPdf(bytes) {
  return hasSignature(bytes, [0x25, 0x50, 0x44, 0x46])
}

function isJpeg(bytes) {
  return hasSignature(bytes, [0xff, 0xd8, 0xff])
}

function isPng(bytes) {
  return hasSignature(bytes, [0x89, 0x50, 0x4e, 0x47])
}

// Walk IFD0 of the EXIF TIFF block looking for the Orientation tag (0x0112).
function readOrientationFromTiff(view, start) {
  if (start + 8 > view.byteLength) {
    return 1
  }

  const littleEndian = view.getUint16(start) === 0x4949
  const directory = start + view.getUint32(start + 4, littleEndian)

  if (directory + 2 > view.byteLength) {
    return 1
  }

  const entryCount = view.getUint16(directory, littleEndian)

  for (let i = 0; i < entryCount; i += 1) {
    const entry = directory + 2 + i * 12

    if (entry + 12 > view.byteLength) {
      return 1
    }

    if (view.getUint16(entry, littleEndian) === 0x0112) {
      return view.getUint16(entry + 8, littleEndian)
    }
  }

  return 1
}

// Scan the JPEG's marker segments for the APP1 block that carries EXIF. Returns 1 (already upright)
// for any image without a readable orientation tag.
function readJpegOrientation(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
    return 1
  }

  let offset = 2

  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset)

    // Start of scan: image data begins here, so every metadata segment has already been passed.
    if (marker === 0xffda) {
      return 1
    }

    const size = view.getUint16(offset + 2)

    // APP1, whose payload starts with "Exif" followed by two padding bytes, then the TIFF header.
    if (marker === 0xffe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
      return readOrientationFromTiff(view, offset + 10)
    }

    if (size < 2) {
      return 1
    }

    offset += 2 + size
  }

  return 1
}

// Dimensions from the JPEG's own frame header — the stored pixels, before any EXIF rotation.
function readJpegSize(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const frameMarkers = [0xffc0, 0xffc1, 0xffc2, 0xffc3, 0xffc5, 0xffc6, 0xffc7, 0xffc9, 0xffca, 0xffcb, 0xffcd, 0xffce, 0xffcf]
  let offset = 2

  while (offset + 9 <= view.byteLength) {
    const marker = view.getUint16(offset)

    if (marker === 0xffda) {
      return null
    }

    if (frameMarkers.indexOf(marker) !== -1) {
      return { width: view.getUint16(offset + 7), height: view.getUint16(offset + 5) }
    }

    const size = view.getUint16(offset + 2)

    if (size < 2) {
      return null
    }

    offset += 2 + size
  }

  return null
}

// Phone cameras store the sensor's raw pixels plus an EXIF orientation tag rather than rotating the
// pixels themselves, and leave it to the viewer to apply. pdf-lib embeds the raw pixels and ignores
// the tag, so an uncorrected photo lands sideways.
//
// The catch is that createImageBitmap may or may not have applied the rotation already: the spec
// default for imageOrientation changed to 'from-image', and not every engine honours a 'none'
// request. Rotating an already-corrected bitmap a second time is just as wrong as not rotating it
// at all, so compare what came back against the JPEG's frame header to see which happened.
async function bakeOrientation(blob, orientation, storedSize) {
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' })
  const { width, height } = bitmap

  // Orientations 5-8 transpose the image, so the corrected pixels swap width and height.
  const swapsAxes = orientation >= 5
  const transposed = storedSize !== null && width === storedSize.height && height === storedSize.width
  const decoderAlreadyRotated = swapsAxes && transposed
  const canvas = document.createElement('canvas')
  canvas.width = swapsAxes && !decoderAlreadyRotated ? height : width
  canvas.height = swapsAxes && !decoderAlreadyRotated ? width : height

  const context = canvas.getContext('2d')

  // Canonical EXIF orientation transforms, expressed against the image's own dimensions.
  switch (decoderAlreadyRotated ? 1 : orientation) {
    case 2:
      context.transform(-1, 0, 0, 1, width, 0)
      break
    case 3:
      context.transform(-1, 0, 0, -1, width, height)
      break
    case 4:
      context.transform(1, 0, 0, -1, 0, height)
      break
    case 5:
      context.transform(0, 1, 1, 0, 0, 0)
      break
    case 6:
      context.transform(0, 1, -1, 0, height, 0)
      break
    case 7:
      context.transform(0, -1, -1, 0, height, width)
      break
    case 8:
      context.transform(0, -1, 1, 0, 0, width)
      break
    default:
      break
  }

  context.drawImage(bitmap, 0, 0)

  if (bitmap.close) {
    bitmap.close()
  }

  const rotated = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))

  return new Uint8Array(await rotated.arrayBuffer())
}

// pdf-lib can only embed JPEG and PNG. Anything else the browser can decode (webp, gif, bmp, and
// HEIC on platforms that support it) is re-encoded to PNG through a canvas first.
async function convertToPngBytes(blob) {
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  canvas.getContext('2d').drawImage(bitmap, 0, 0)

  if (bitmap.close) {
    bitmap.close()
  }

  const pngBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))

  return new Uint8Array(await pngBlob.arrayBuffer())
}

async function embedImage(pdfDoc, blob, bytes) {
  if (isJpeg(bytes)) {
    const orientation = readJpegOrientation(bytes)

    // Only re-encode when there is a rotation to apply, so upright photos keep their original bytes.
    if (orientation > 1) {
      return pdfDoc.embedJpg(await bakeOrientation(blob, orientation, readJpegSize(bytes)))
    }

    return pdfDoc.embedJpg(bytes)
  }

  if (isPng(bytes)) {
    return pdfDoc.embedPng(bytes)
  }

  return pdfDoc.embedPng(await convertToPngBytes(blob))
}

// Give the page the same orientation as the image, then centre the image inside the margins.
function addImagePage(pdfDoc, image) {
  const landscape = image.width > image.height
  const pageWidth = landscape ? PAGE_LONG_EDGE : PAGE_SHORT_EDGE
  const pageHeight = landscape ? PAGE_SHORT_EDGE : PAGE_LONG_EDGE
  const page = pdfDoc.addPage([pageWidth, pageHeight])
  const scale = Math.min((pageWidth - PAGE_MARGIN * 2) / image.width, (pageHeight - PAGE_MARGIN * 2) / image.height)
  const width = image.width * scale
  const height = image.height * scale

  page.drawImage(image, {
    x: (pageWidth - width) / 2,
    y: (pageHeight - height) / 2,
    width,
    height,
  })
}

async function addFileToPdf(pdfDoc, blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer())

  // Pages from an attached PDF are copied in at their original size.
  if (isPdf(bytes)) {
    const source = await PDFDocument.load(bytes)
    const pages = await pdfDoc.copyPages(source, source.getPageIndices())
    pages.forEach(page => pdfDoc.addPage(page))

    return
  }

  addImagePage(pdfDoc, await embedImage(pdfDoc, blob, bytes))
}

/**
 * Combine a ticket's uploaded documents into a single PDF, in the order given.
 *
 * Rejects if any document can't be fetched, rather than quietly producing a PDF with a page
 * missing — for a document of record, a short download is worse than a failed one.
 *
 * @param {Array<{url: string, fileName: string}>} files
 * @returns {Promise<Blob>} The combined PDF.
 */
export default async function buildTicketDocumentsPdf(files) {
  // Fetch up front so pages land in upload order rather than whichever request finishes first.
  const downloads = await Promise.all(
    files.map(async file => {
      const response = await fetch(file.url)

      if (!response.ok) {
        throw new Error(`Could not fetch ${file.fileName} (${response.status})`)
      }

      return response.blob()
    }),
  )

  const pdfDoc = await PDFDocument.create()

  await downloads.reduce(async (previous, blob) => {
    await previous

    return addFileToPdf(pdfDoc, blob)
  }, Promise.resolve())

  const pdfBytes = await pdfDoc.save()

  return new Blob([pdfBytes], { type: 'application/pdf' })
}
